/**
 * Score.jsx
 * The dial, and the breakdown beneath it.
 *
 * The single most important thing on this screen is that the number never
 * outruns its evidence. So:
 *
 *   - the dial says what it rests on, every time, in words, underneath it
 *   - a factor drawn from the account's own posts carries its sample size
 *   - a factor judged by a model carries a sparkle and no sample
 *   - when too few factors could be scored, there is no number at all
 *
 * There is deliberately no "predicted reach". The app cannot know that, and a
 * precise-looking figure would be the most persuasive wrong thing here.
 *
 * Dependencies: components/Icons.jsx
 */

import { Chart, Clock, Hash, Layers, Sparkle, Target, Warning, Edit, Send } from '../Icons.jsx';

const TONE = { ok: 'ok', warn: 'warn', danger: 'danger' };

const ICONS = {
  hook: Target,
  caption: Edit,
  format: Layers,
  time: Clock,
  cta: Send,
  hashtags: Hash,
};

/** The ring. Plain SVG — one arc does not need a chart library. */
function Dial({ score, band }) {
  const size = 168;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const filled = score === null ? 0 : (score / 100) * circumference;

  return (
    <div className="dial">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle
          className="dial__track"
          cx={size / 2}
          cy={size / 2}
          r={r}
          strokeWidth={stroke}
          fill="none"
        />
        {score !== null && (
          <circle
            className={`dial__fill dial__fill--${band?.tone ?? 'ok'}`}
            cx={size / 2}
            cy={size / 2}
            r={r}
            strokeWidth={stroke}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={`${filled} ${circumference}`}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        )}
      </svg>

      <div className="dial__label">
        {score === null ? (
          <>
            <span className="dial__none">—</span>
            <span className="dial__band">Not enough to score</span>
          </>
        ) : (
          <>
            <span className="dial__value">
              {score}
              <small>/100</small>
            </span>
            <span className={`dial__band dial__band--${band?.tone ?? 'ok'}`}>{band?.label}</span>
          </>
        )}
      </div>
    </div>
  );
}

export default function Score({ result, baseline, onCritique, critiquing, hasCritique }) {
  if (!result) {
    return (
      <section className="card">
        <div className="empty empty--inline">
          <div className="empty__art">
            <Chart size={26} />
          </div>
          <h3>Nothing scored yet</h3>
          <p>Write a caption and press Assess draft.</p>
        </div>
      </section>
    );
  }

  const { overall, band, factors, basis, typical } = result;

  return (
    <section className="card">
      <div className="card__head">
        <span className="tile">
          <Chart size={21} />
        </span>
        <div className="card__head-text">
          <h2>How this compares</h2>
          <p>Measured against your own published posts.</p>
        </div>
      </div>

      <div className="score">
        <Dial score={overall} band={band} />

        <div className="score__text">
          {overall === null ? (
            <>
              <h3>Not enough to give a figure</h3>
              <p>
                Only {basis.scoredFactors} of {basis.totalFactors} factors could be judged. A
                score from fewer would be the most confident-looking thing on this screen, and
                the least earned.
              </p>
            </>
          ) : (
            <>
              <h3>
                {band?.label === 'Excellent' || band?.label === 'Good'
                  ? 'This sits above your usual'
                  : 'This sits below your usual'}
              </h3>
              <p>
                Based on {basis.posts} of your published posts, and {basis.scoredFactors} of{' '}
                {basis.totalFactors} factors.
              </p>
            </>
          )}

          {/* The sentence the whole feature hangs on. */}
          <p className="score__caveat">
            <Warning size={13} /> This is a comparison with your own history, not a prediction.
            Nothing here knows how this post will do.
          </p>
        </div>
      </div>

      {/* What comparable posts actually did. History, and labelled as such. */}
      {typical?.sample > 0 && (
        <div className="typical">
          <div className="typical__head">
            What your {typical.sameFormat ? 'posts in this format' : 'posts'} have actually done
            <span>{typical.sample} measured</span>
          </div>
          <div className="typical__row">
            {[
              ['Median reach', typical.reach],
              ['Median impressions', typical.impressions],
              ['Median engagements', typical.engagements],
              ['Engagement rate', typical.rate === null ? null : `${typical.rate.toFixed(1)}%`],
            ].map(([label, value]) => (
              <div key={label}>
                <span className="typical__num">
                  {value === null || value === undefined
                    ? '—'
                    : typeof value === 'string'
                      ? value
                      : Math.round(value).toLocaleString()}
                </span>
                <span className="typical__cap">{label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="factors">
        {factors.map((f) => {
          const Icon = ICONS[f.key] ?? Chart;
          return (
            <div className="factor" key={f.key}>
              <span className="factor__icon">
                <Icon size={15} />
              </span>

              <div className="factor__text">
                <div className="factor__top">
                  <span className="factor__label">
                    {f.label}
                    {f.basis === 'judged' && (
                      <span className="factor__ai" title="An opinion from a model, not a measurement">
                        <Sparkle size={11} />
                      </span>
                    )}
                  </span>

                  {f.score === null ? (
                    <span className="tag tag--muted">Not judged</span>
                  ) : (
                    <>
                      <span className={`tag tag--${TONE[f.band?.tone] ?? 'muted'}`}>
                        {f.band?.label}
                      </span>
                      <span className="factor__score">{f.score}/100</span>
                    </>
                  )}
                </div>

                <div className="factor__bar">
                  <span
                    className={`factor__fill factor__fill--${f.band?.tone ?? 'muted'}`}
                    style={{ width: `${f.score ?? 0}%` }}
                  />
                </div>

                <div className="factor__why">
                  {f.why}
                  {f.basis === 'measured' && f.sample > 0 && (
                    <span className="factor__sample"> · {f.sample} posts</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Hook and CTA cannot be counted, only judged — so they are a separate,
          paid, opt-in step rather than something that happens silently. */}
      {!hasCritique && (
        <button className="btn btn--primary" onClick={onCritique} disabled={critiquing}>
          <Sparkle size={16} />
          {critiquing ? 'Reading the draft…' : 'Add AI review of hook and call to action'}
        </button>
      )}

      {baseline && !baseline.enough && (
        <div className="notice notice--error">
          <Warning />
          <span>
            Only {baseline.measured} measured {baseline.platform} posts. Below{' '}
            {baseline.minSample} the data-driven factors stay unjudged — there is not enough here
            to tell a pattern from a coincidence.
          </span>
        </div>
      )}
    </section>
  );
}
